<template>
  <div>
    <b-modal id="bv-modal-consent" :title="title" :visible="visible" scrollable centered hideHeaderClose
      no-close-on-backdrop no-close-on-esc hide-footer>
      <div>
        <b-form v-if="validations !== undefined" @submit.stop.prevent="onSubmit" novalidate>
          <div v-html="sanitizedContent(subtitle)"></div>
          <b-form-group v-for="item of form" :key="item.inputId" :id="`${item.inputId}-group`"
            :description="item.description" :label="item.label" :label-for="item.inputId">

            <!-- If type=date -->
            <b-input-group v-if="item.type === 'date'">
              <b-form-input :id="item.inputId" :name="item.inputName" :placeholder="item.placeholder"
                v-model="$v.formModel[item.inputName].$model" type="text" autocomplete="off"
                :aria-describedby="`${item.inputId}-live-feedback`" :state="validateState(item.inputName)" v-mask="item.mask" />
              <b-form-invalid-feedback :id="`${item.inputId}-live-feedback`">
                <div v-for="(message, ruleKey) in validationRuleMessages?.[item.inputName]" :key="ruleKey"
                  v-show="!$v.formModel[item.inputName][ruleKey]">
                  {{ message }}
                </div>
              </b-form-invalid-feedback>
            </b-input-group>

            <!-- Else-If type=select -->
            <b-form-select v-else-if="item.type === 'select'" :id="item.inputId" :name="item.inputName"
              :placeholder="item.placeholder" v-model="$v.formModel[item.inputName].$model"
              :state="validateState(item.inputName)" :aria-describedby="`${item.inputId}-live-feedback`"
              :options="item.options"></b-form-select>

            <!-- Else, render the input type as it is -->
            <b-form-input v-else :type="item.type" trim :id="item.inputId" :name="item.inputName"
              :placeholder="item.placeholder" v-model="$v.formModel[item.inputName].$model"
              :state="validateState(item.inputName)" :aria-describedby="`${item.inputId}-live-feedback`"  v-mask="item.mask">
            </b-form-input>
            <!-- Error Messages -->
            <b-form-invalid-feedback :id="`${item.inputId}-live-feedback`">
              <div v-for="(message, ruleKey) in validationRuleMessages?.[item.inputName]" :key="ruleKey"
                v-show="!$v.formModel[item.inputName][ruleKey]">
                {{ message }}
              </div>
            </b-form-invalid-feedback>
          </b-form-group>

          <b-button-toolbar class="pt-4">
            <b-button-group class="mx-1">
              <b-button @click="back" variant="outline-secondary">{{ cancelFormButton.text }}</b-button>
            </b-button-group>
            <b-button-group>
              <b-button class="default-button" type="submit">{{ submitFormButton.text }}</b-button>
            </b-button-group>
            <loading :show="loading" />
          </b-button-toolbar>
        </b-form>
      </div>
    </b-modal>
    <!-- Patient Not Found Modal -->
    <patient-not-found-modal :okButtonText="patientNotFoundOKButton.text"
      :cancelButtonText="patientNotFoundCancelButton.text" :visible="showNotFoundModal"
      :description="patientNotFoundDescription" :title="patientNotFoundTitle" @back="
        visible = true
      showNotFoundModal = false
        "
			:locale="locale" />

		<patient-already-enrolled-modal 
			:okButtonText="patientNotFoundOKButton.text"
      :cancelButtonText="patientNotFoundCancelButton.text" :visible="showAlreadyEnrolledModal"
      :description="patientAlreadyEnrolledDescription" :title="patientAlreadyEnrolledTitle" @back="
        visible = true
      showAlreadyEnrolledModal = false
        "
			:locale="locale"  />

		<patient-eligible-modal 
			:okButtonText="patientEligibleOKButton.text"
      :description="patientEligibleDescription" 
			:title="patientEligibleTitle" 
			:visible="showEligibleModal"
			@ok="() => this.showEligibleModal = false" />

  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import Validators from '@/utils/customFormValidations'
import { msmApi, strapi } from '@/api'
import PatientNotFoundModal from '@/components/RemoteConsent/PatientNotFoundModal.vue'
import PatientAlreadyEnrolledModal from '@/components/RemoteConsent/PatientAlreadyEnrolledModal.vue'
import PatientEligibleModal from '@/components/RemoteConsent/PatientEligibleModal.vue'
import Loading from '../Loading.vue'

const touchMap = new WeakMap()

export default {
  components: {
    PatientNotFoundModal,
		PatientAlreadyEnrolledModal,
		PatientEligibleModal,
    Loading
  },
  props: {
    title: String,
    subtitle: String,
    form: Array,
    patientNotFoundTitle: String,
    patientNotFoundDescription: String,
		patientAlreadyEnrolledTitle: String,
    patientAlreadyEnrolledDescription: String,
		patientEligibleTitle: String,
    patientEligibleDescription: String,
    patientNotFoundOKButton: Object,
    patientNotFoundCancelButton: Object,
		patientEligibleOKButton: Object,
    submitFormButton: Object,
    cancelFormButton: Object,
    locale: String
  },
  data() {
    return {
      formModel: undefined,
      loading: false,
      showNotFoundModal: false,
			showAlreadyEnrolledModal: false,
			showEligibleModal: false,
      validations: undefined,
      visible: true,
      formatted: '',
      selected: '',
    }
  },
  computed: {
    maxDate() {
      const today = new Date()
      today.setFullYear(today.getFullYear() - 18)
      return today
    }
  },
  created() {
    this.formModel = this.form.reduce((model, curr) => ({ ...model, [curr.inputName]: curr.defaultValue }), {})
    strapi
      .getFormValidation('CheckEligibilityForm', this.locale)
      .then(validationRules => {
        this.validationRuleMessages = validationRules
      })
      .then(() => {
        this.validations = Object
          .entries(this.validationRuleMessages)
          .reduce((validations, [inputName, rulesObj]) =>
          ({
            ...validations, [inputName]: Object.keys(rulesObj)
              .filter(key => key in Validators)
              .reduce((validators, key) => ({ ...validators, [key]: Validators[key] }), {})
          }), {}
          )
      })
  },
  methods: {
    onContext(ctx) {
      this.formatted = ctx.selectedFormatted
      this.selected = ctx.selectedYMD
    },
    back() {
      this.$router.back()
    },
    delayTouch($v) {
      $v.$reset()
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v))
      }
      touchMap.set($v, setTimeout($v.$touch, 500))
    },
    onSubmit() {
      this.$v.formModel.$touch()

      if (this.$v.formModel.$anyError) {
        return
      }
      this.loading = true
      msmApi
        .checkEligibility(this.formModel)
        .then(({ data }) => {
          if (data.recordFound === false) {
            this.showNotFoundModal = true
            return
          }
					else if (data.alreadyEnrolled === true) {
						this.showAlreadyEnrolledModal = true
						return
					}
					else {
						this.showEligibleModal = true
						this.$root.$emit('eligible', this.formModel)
					}
        })
        .catch((error) => {
          this.showNotFoundModal = true
          this.$gtag.exception({
            description: JSON.stringify(error),
            fatal: true
          })
        })
        .finally(() => {
          this.loading = false
          this.visible = false
        })
    },
    sanitizedContent(content) {
      return DOMPurify.sanitize(marked(content))
    },
    validateState(fieldName) {
      const field = this.$v.formModel[fieldName]
      return field.$dirty ? !field.$error : null
    }
  },
  validations() {
    return {
      formModel: this.validations || {}
    }
  }
}
</script>
